"use client";
import React, { useState, useEffect, useRef } from "react";
import { Box, Container, Typography, Grid, Divider } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const theme = createTheme({
  palette: { primary: { main: "#8b5cf6" } },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const StatCard = ({ value, label, description, isArabic }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  const getNumericValue = (val) => {
    const match = val.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const targetValue = getNumericValue(value);
  const suffix = value.replace(/\d+/, "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const duration = 3000;
          const steps = 100;
          const stepDuration = duration / steps;
          let currentStep = 0;

          const timer = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const newCount = Math.floor(targetValue * easedProgress);
            setCount(newCount);
            if (currentStep >= steps) {
              setCount(targetValue);
              clearInterval(timer);
            }
          }, stepDuration);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.3 },
    );

    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [targetValue, hasAnimated]);

  return (
    <Box
      ref={ref}
      sx={{
        textAlign: isArabic ? "right" : "left",
        px: 3,
        py: 3,
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: "4rem", md: "6rem", lg: "4rem", xl: "5rem" },
          fontWeight: 500,
          color: "#9048FB",
          lineHeight: 1,
          mb: 1,
          fontFamily: "var(--font-plusjakarta)",
        }}
      >
        {count}
        {suffix}
      </Typography>
      <Typography
        sx={{
          color: "#9048FB",
          fontSize: { xs: "1rem", md: "1.4rem" },
          fontWeight: 500,
          mb: 2,
          fontFamily: "var(--font-plusjakarta)",
          ml: isArabic ? 0 : "4%",
          mr: isArabic ? "4%" : 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#405261",
          fontSize: { xs: "0.875rem", md: "1rem" },
          lineHeight: 1.6,
          maxWidth: "300px",
          fontFamily: "var(--font-outfit)",
          ml: isArabic ? 0 : "4%",
          mr: isArabic ? "4%" : "auto",
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

export default function StatsDisplay() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const statsRaw = t("statsDisplay.stats", { returnObjects: true });
  const stats = Array.isArray(statsRaw) ? statsRaw : [];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#ffffff",
          py: { xs: 6, md: 2 },
          direction: isArabic ? "rtl" : "ltr",
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={{ xs: 4, md: 0 }}
            justifyContent="center"
            alignItems="flex-start"
          >
            {/* Left Divider */}
            <Grid
              item
              md="auto"
              sx={{ display: { xs: "none", md: "flex" }, alignItems: "left" }}
            >
              <Divider
                orientation="vertical"
                sx={{
                  height: "180px",
                  borderColor: "#EDE8E6",
                  borderWidth: "1px",
                }}
              />
            </Grid>

            {stats.map((stat, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12} md={4}>
                  <StatCard
                    value={stat.value}
                    label={stat.label}
                    description={stat.description}
                    isArabic={isArabic}
                  />
                </Grid>
                {index < stats.length - 1 && (
                  <Grid
                    item
                    md="auto"
                    sx={{
                      display: { xs: "none", md: "flex" },
                      alignItems: "center",
                    }}
                  >
                    <Divider
                      orientation="vertical"
                      sx={{
                        height: "180px",
                        borderColor: "#EDE8E6",
                        borderWidth: "1px",
                      }}
                    />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
